import React, { useEffect, useState } from "react";
import {
  MessageCircle,
  Mail,
  Globe,
  Phone,
  Brain,
  User,
  Clock,
  ArrowUpRight,
  Tag,
} from "lucide-react";

const inboxItems = [
  {
    channel: "whatsapp",
    name: "Priya Sharma",
    last: "Refund processed — thanks!",
    time: "2m",
    unread: 0,
    status: "resolved",
    tags: ["billing"],
  },
  {
    channel: "email",
    name: "Marcus Weber",
    last: "Can you check my invoice…",
    time: "7m",
    unread: 2,
    status: "active",
    tags: ["billing", "vip"],
  },
  {
    channel: "web",
    name: "Ananya Iyer",
    last: "Dashboard not loading agent…",
    time: "12m",
    unread: 1,
    status: "human",
    tags: ["bug"],
  },
  {
    channel: "voice",
    name: "Daniel Ortiz",
    last: "Voicemail transcript attached",
    time: "25m",
    unread: 0,
    status: "active",
    tags: ["sales"],
  },
  {
    channel: "whatsapp",
    name: "Tomás Silva",
    last: "Is there a family plan?",
    time: "1h",
    unread: 0,
    status: "resolved",
    tags: ["pricing"],
  },
];

const channelIcon = {
  whatsapp: MessageCircle,
  email: Mail,
  web: Globe,
  voice: Phone,
};

const statusMeta = {
  resolved: { label: "Resolved", color: "#3DDFB3" },
  active: { label: "AI Handling", color: "#7C5CFF" },
  human: { label: "Human Takeover", color: "#F97316" },
};

export default function Omnichannel() {
  const [active, setActive] = useState(1);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return;
    const t = setInterval(
      () => setActive((a) => (a + 1) % inboxItems.length),
      3200
    );
    return () => clearInterval(t);
  }, [paused]);
  const sel = inboxItems[active];
  const Icon = channelIcon[sel.channel];

  return (
    <section
      id="omnichannel"
      data-testid="omnichannel-section"
      className="relative py-28 md:py-40 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="max-w-3xl mb-16">
          <div className="inline-flex items-center gap-2 mb-5">
            <span className="text-[11px] font-mono tracking-[0.2em] uppercase text-[#3DDFB3]">
              ── Omnichannel inbox
            </span>
          </div>
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight text-white">
            One <span className="italic">conversation</span>.<br />
            Every channel, one customer.
          </h2>
          <p className="mt-6 text-white/60 text-lg max-w-2xl">
            Your agent sees every past email, every WhatsApp reply, every tool
            call — so when the customer says "as I mentioned yesterday", it
            actually knows what they mean.
          </p>
        </div>

        {/* 3-column inbox */}
        <div
          className="grid grid-cols-1 lg:grid-cols-[300px_1fr_300px] rounded-2xl border border-white/10 overflow-hidden bg-[#0b0b13]"
          data-testid="omnichannel-inbox"
        >
          {/* LEFT: conversation list */}
          <div className="border-b lg:border-b-0 lg:border-r border-white/5">
            <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between text-[11px] font-mono uppercase tracking-[0.12em] text-white/40">
              <span>Inbox · 12</span>
              <span>Unread · 3</span>
            </div>
            <div className="divide-y divide-white/5">
              {inboxItems.map((it, i) => {
                const IC = channelIcon[it.channel];
                const st = statusMeta[it.status];
                const isActive = i === active;
                return (
                  <button
                    key={i}
                    onClick={() => {
                      setActive(i);
                      setPaused(true);
                    }}
                    data-testid={`inbox-item-${i}`}
                    className={`w-full text-left px-4 py-3.5 transition-colors ${
                      isActive ? "bg-white/[0.04]" : "hover:bg-white/[0.02]"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span
                          className="w-6 h-6 rounded-md flex items-center justify-center"
                          style={{
                            background: `${st.color}14`,
                            border: `1px solid ${st.color}40`,
                          }}
                        >
                          <IC className="w-3 h-3" style={{ color: st.color }} />
                        </span>
                        <span className="text-[13px] text-white font-medium">
                          {it.name}
                        </span>
                      </div>
                      <span className="text-[11px] text-white/40">{it.time}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[12px] text-white/50 truncate pr-3">
                        {it.last}
                      </span>
                      {it.unread > 0 && (
                        <span className="text-[10px] font-mono bg-[#7C5CFF] text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                          {it.unread}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* MIDDLE: thread */}
          <div className="min-h-[520px] flex flex-col">
            <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-[12px] font-semibold">
                  {sel.name
                    .split(" ")
                    .map((p) => p[0])
                    .join("")}
                </div>
                <div>
                  <div className="text-[14px] font-medium">{sel.name}</div>
                  <div className="flex items-center gap-2 text-[11px] text-white/50">
                    <Icon
                      className="w-3 h-3"
                      style={{ color: statusMeta[sel.status].color }}
                    />
                    {sel.channel} ·{" "}
                    <span style={{ color: statusMeta[sel.status].color }}>
                      {statusMeta[sel.status].label}
                    </span>
                  </div>
                </div>
              </div>
              <button
                className="btn-ghost text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 text-white/80"
                data-testid="takeover-btn"
              >
                <User className="w-3 h-3" /> Take over
              </button>
            </div>

            <div className="flex-1 px-6 py-6 space-y-4 bg-[radial-gradient(circle_at_80%_0%,rgba(124,92,255,0.05),transparent_50%)]">
              <Bubble side="user" name={sel.name} time="09:42">
                Hey — still waiting on the duplicate charge for A-2381.
              </Bubble>
              <Bubble side="ai" name="CRM Agent" time="09:42">
                I found the duplicate on 11 Oct. Refunding ₹2,499 to card
                •••4421 now — ETA 3 business days. Confirming by email too.
              </Bubble>
              <Bubble side="user" name={sel.name} time="09:43">
                Amazing, thank you!
              </Bubble>
              <Bubble side="ai" name="CRM Agent" time="09:43">
                Any time. Marking this resolved — have a great day, Priya.
              </Bubble>
            </div>

            <div className="px-6 py-3 border-t border-white/5 flex items-center gap-2">
              <input
                data-testid="reply-input"
                readOnly
                placeholder="Reply as agent or Cmd+K to use AI…"
                className="flex-1 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-[13px] text-white/80 placeholder:text-white/30 focus:outline-none focus:border-[#7C5CFF]/60"
              />
              <button className="btn-primary text-white text-xs font-medium px-4 py-2 rounded-lg inline-flex items-center gap-1.5">
                Send <ArrowUpRight className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* RIGHT: context panel */}
          <div className="border-t lg:border-t-0 lg:border-l border-white/5 p-5 space-y-5">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-[0.12em] text-white/40 mb-3">
                Customer
              </div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-[#3DDFB3]/15 border border-[#3DDFB3]/30 flex items-center justify-center text-[13px] font-semibold text-[#3DDFB3]">
                  {sel.name
                    .split(" ")
                    .map((p) => p[0])
                    .join("")}
                </div>
                <div>
                  <div className="text-[13px] font-medium">{sel.name}</div>
                  <div className="text-[11px] text-white/45">
                    Pro · joined Mar 2024
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <Stat k="Tickets" v="14" />
                <Stat k="CSAT" v="4.9" />
                <Stat k="MRR" v="$89" />
                <Stat k="Risk" v="Low" tone="#3DDFB3" />
              </div>
            </div>

            <div>
              <div className="text-[11px] font-mono uppercase tracking-[0.12em] text-white/40 mb-3">
                Tags
              </div>
              <div className="flex flex-wrap gap-1.5">
                {["billing", "vip", "whatsapp"].map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-white/70"
                  >
                    <Tag className="w-2.5 h-2.5" /> {t}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <div className="text-[11px] font-mono uppercase tracking-[0.12em] text-white/40 mb-3">
                Activity
              </div>
              <ul className="space-y-2 text-[12px] text-white/60">
                <li className="flex gap-2">
                  <Clock className="w-3 h-3 mt-0.5 text-white/30" />
                  Refund triggered · 09:42
                </li>
                <li className="flex gap-2">
                  <Clock className="w-3 h-3 mt-0.5 text-white/30" />
                  KB: refunds_policy_v3
                </li>
                <li className="flex gap-2">
                  <Clock className="w-3 h-3 mt-0.5 text-white/30" />
                  Shopify · order.fetched
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Bubble({ side, name, time, children }) {
  const isAI = side === "ai";
  return (
    <div className={`flex items-start gap-3 ${isAI ? "" : "flex-row"}`}>
      <div
        className={`w-7 h-7 rounded-md flex items-center justify-center text-[11px] font-semibold ${
          isAI
            ? "bg-[#7C5CFF] text-white shadow-[0_0_12px_rgba(124,92,255,0.5)]"
            : "bg-white/10 text-white/80"
        }`}
      >
        {isAI ? <Brain className="w-3.5 h-3.5" /> : name[0]}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 text-[11px] text-white/40 mb-1">
          <span>{name}</span>
          <span>·</span>
          <span>{time}</span>
        </div>
        <div
          className={`inline-block max-w-[88%] px-3.5 py-2.5 rounded-2xl text-[13.5px] leading-relaxed ${
            isAI
              ? "bg-[#7C5CFF]/12 border border-[#7C5CFF]/25 text-white/90 rounded-tl-sm"
              : "bg-white/[0.05] border border-white/5 text-white/90 rounded-tl-sm"
          }`}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

function Stat({ k, v, tone = "#ffffff" }) {
  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-lg px-2.5 py-2">
      <div className="text-[10px] uppercase tracking-wider text-white/40">
        {k}
      </div>
      <div className="text-[14px] font-medium" style={{ color: tone }}>
        {v}
      </div>
    </div>
  );
}
